import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import {
  createInvestigatorSession,
  deleteInvestigatorSession,
  exportInvestigatorSession,
  getInvestigatorSession,
  listInvestigatorSessions,
  renameInvestigatorSession,
  sendInvestigatorMessage,
} from './investigatorApi';
import type { InvestigatorChatMessage } from './types';
import { formatCopilotPlainText } from '../copilot/formatCopilotText';
import { Logger } from '../../../lib/utils';
import { ChatSessionSidebar } from '../shared/ChatSessionSidebar';
import { ChatSessionActions } from '../shared/ChatSessionActions';
import type { AiChatSessionSummary } from '../shared/chatHistoryTypes';
import { mapStoredMessagesToInvestigator } from '../shared/mapStoredMessages';
import { useDebouncedValue } from '../shared/useDebouncedValue';

type InvestigatorPanelProps = {
  open: boolean;
  onClose: () => void;
};

function nextMessageId(): string {
  return `inv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const InvestigatorPanel = ({ open, onClose }: InvestigatorPanelProps) => {
  const location = useLocation();
  const [sessions, setSessions] = useState<AiChatSessionSummary[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<InvestigatorChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialLoadRef = useRef(true);
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  const activeSession = sessions.find((session) => session.id === sessionId);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const loadSessionMessages = useCallback(async (id: string) => {
    setLoadingMessages(true);
    setError(null);
    try {
      const detail = await getInvestigatorSession(id);
      setSessionId(detail.id);
      setMessages(mapStoredMessagesToInvestigator(detail.messages));
    } catch (err) {
      Logger.debug('No se pudo cargar sesión investigador', { err });
      setError('No se pudo cargar la conversación.');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const refreshSessions = useCallback(
    async (query?: string, selectLatest = false) => {
      setLoadingSessions(true);
      try {
        const list = await listInvestigatorSessions(query);
        setSessions(list);
        if (selectLatest && list.length > 0) {
          await loadSessionMessages(list[0].id);
        }
        return list;
      } catch (err) {
        Logger.debug('No se pudo listar sesiones investigador', { err });
        setError('No se pudo cargar el historial.');
        return [];
      } finally {
        setLoadingSessions(false);
      }
    },
    [loadSessionMessages],
  );

  useEffect(() => {
    if (!open) {
      initialLoadRef.current = true;
      return;
    }

    let cancelled = false;
    void (async () => {
      const selectLatest = initialLoadRef.current && !debouncedSearch.trim();
      initialLoadRef.current = false;
      const list = await refreshSessions(debouncedSearch.trim() || undefined, selectLatest);
      if (cancelled) return;
      if (selectLatest && list.length === 0) {
        setSessionId(null);
        setMessages([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, debouncedSearch, refreshSessions]);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleNewSession = async () => {
    setError(null);
    try {
      const session = await createInvestigatorSession({ routeContext: location.pathname });
      const summary: AiChatSessionSummary = {
        id: session.id,
        title: session.title,
        routeContext: session.routeContext,
        createdAt: session.createdAt,
        updatedAt: session.updatedAt,
        messageCount: 0,
      };
      setSessions((prev) => [summary, ...prev]);
      setSessionId(session.id);
      setMessages([]);
    } catch (err) {
      Logger.debug('No se pudo crear sesión investigador', { err });
      setError('No se pudo iniciar una nueva investigación.');
    }
  };

  const handleSelectSession = (id: string) => {
    if (id === sessionId || loadingMessages) return;
    void loadSessionMessages(id);
  };

  const handleRenameSession = async (title: string) => {
    if (!sessionId) return;
    setRenaming(true);
    setError(null);
    try {
      const updated = await renameInvestigatorSession(sessionId, title);
      setSessions((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
    } catch (err) {
      Logger.debug('No se pudo renombrar sesión investigador', { err });
      setError('No se pudo renombrar la conversación.');
    } finally {
      setRenaming(false);
    }
  };

  const handleExportSession = async (format: 'md' | 'txt') => {
    if (!sessionId) return;
    setExporting(true);
    setError(null);
    try {
      await exportInvestigatorSession(sessionId, format);
    } catch (err) {
      Logger.debug('No se pudo exportar sesión investigador', { err });
      setError('No se pudo exportar la conversación.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteSession = async (id: string) => {
    if (deletingSessionId) return;
    setDeletingSessionId(id);
    setError(null);
    try {
      await deleteInvestigatorSession(id);
      const remaining = sessions.filter((s) => s.id !== id);
      setSessions(remaining);
      if (sessionId === id) {
        if (remaining.length > 0) {
          await loadSessionMessages(remaining[0].id);
        } else {
          setSessionId(null);
          setMessages([]);
        }
      }
    } catch (err) {
      Logger.debug('No se pudo eliminar sesión investigador', { err });
      setError('No se pudo eliminar la conversación.');
    } finally {
      setDeletingSessionId(null);
    }
  };

  const ensureSession = async (): Promise<string | null> => {
    if (sessionId) return sessionId;
    try {
      const session = await createInvestigatorSession({ routeContext: location.pathname });
      const summary: AiChatSessionSummary = {
        id: session.id,
        title: session.title,
        routeContext: session.routeContext,
        createdAt: session.createdAt,
        updatedAt: session.updatedAt,
        messageCount: 0,
      };
      setSessions((prev) => [summary, ...prev]);
      setSessionId(session.id);
      return session.id;
    } catch (err) {
      Logger.debug('No se pudo crear sesión investigador', { err });
      setError('No se pudo iniciar el investigador. Intentá de nuevo.');
      return null;
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const activeSessionId = await ensureSession();
    if (!activeSessionId) return;

    const userMessage: InvestigatorChatMessage = {
      id: nextMessageId(),
      role: 'user',
      text,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const result = await sendInvestigatorMessage(activeSessionId, text);
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          role: 'assistant',
          text: result.text,
        },
      ]);
      setSessions((prev) =>
        prev.map((s) =>
          s.id === activeSessionId
            ? {
                ...s,
                updatedAt: new Date().toISOString(),
                messageCount: s.messageCount + 2,
                title: s.title ?? (text.length > 80 ? `${text.slice(0, 79)}…` : text),
              }
            : s,
        ),
      );
    } catch (err: unknown) {
      Logger.debug('Error enviando mensaje investigador', { err });
      const axiosErr = err as { response?: { data?: { message?: string; code?: string } } };
      const code = axiosErr.response?.data?.code;
      if (code === 'AI_QUOTA_EXCEEDED') {
        setError('Cupo de tokens IA agotado para este período.');
      } else {
        setError(
          axiosErr.response?.data?.message ??
            'El investigador no está disponible en este momento.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className='fixed inset-0 z-40 bg-black/20 md:bg-transparent'
        onClick={onClose}
        aria-hidden='true'
      />
      <aside
        className='fixed top-16 right-0 z-50 flex h-[calc(100vh-4rem)] w-full max-w-2xl flex-col border-l border-border bg-card shadow-lg'
        aria-label='Investigador SGI'
      >
        <header className='flex items-center justify-between border-b border-border px-4 py-3'>
          <div>
            <h2 className='text-sm font-semibold text-foreground'>Investigador SGI</h2>
            <p className='text-xs text-muted-foreground'>
              Investigación profunda — puede tardar 2–5 minutos
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            aria-label='Cerrar investigador'
          >
            <XMarkIcon className='h-5 w-5' />
          </button>
        </header>

        <div className='flex min-h-0 flex-1'>
          <ChatSessionSidebar
            sessions={sessions}
            activeSessionId={sessionId}
            loading={loadingSessions}
            deletingSessionId={deletingSessionId}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSelect={handleSelectSession}
            onNew={() => void handleNewSession()}
            onDelete={(id) => void handleDeleteSession(id)}
          />

          <div className='flex min-w-0 flex-1 flex-col'>
            <ChatSessionActions
              sessionId={sessionId}
              sessionTitle={activeSession?.title ?? null}
              messageCount={activeSession?.messageCount ?? messages.length}
              renaming={renaming}
              exporting={exporting}
              onRename={handleRenameSession}
              onExport={handleExportSession}
            />
            <div className='flex-1 overflow-y-auto px-4 py-3 space-y-3'>
              {loadingMessages && (
                <p className='text-sm text-muted-foreground'>Cargando conversación…</p>
              )}
              {!loadingMessages && !sessionId && messages.length === 0 && !error && (
                <p className='text-sm text-muted-foreground'>
                  Formulá una pregunta compleja: el agente planificará búsquedas en varios módulos
                  y cruzará evidencia antes de responder. Tus investigaciones quedan guardadas.
                </p>
              )}
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`rounded-lg px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'ml-8 bg-primary text-primary-foreground'
                      : 'mr-8 bg-muted text-foreground'
                  }`}
                >
                  <p className='whitespace-pre-wrap break-words'>
                    {formatCopilotPlainText(msg.text)}
                  </p>
                </div>
              ))}
              {loading && (
                <p className='text-sm text-muted-foreground'>Investigando…</p>
              )}
              <div ref={messagesEndRef} />
            </div>

            {error && (
              <p className='px-4 py-2 text-sm text-destructive border-t border-border'>{error}</p>
            )}

            <footer className='border-t border-border p-3'>
              <div className='flex gap-2'>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder='Describí qué querés investigar…'
                  rows={3}
                  disabled={loading || loadingMessages}
                  className='flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50'
                />
                <button
                  type='button'
                  onClick={() => void handleSend()}
                  disabled={!input.trim() || loading || loadingMessages}
                  className='self-end rounded-md bg-primary p-2 text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
                  aria-label='Enviar consulta de investigación'
                >
                  <PaperAirplaneIcon className='h-5 w-5' />
                </button>
              </div>
            </footer>
          </div>
        </div>
      </aside>
    </>
  );
};
